import { getPresentationGroupKey } from './batch.utils';
import { getPresentationHandlers } from './presentationHandlers';
import type { ActivationBatch } from './state/activationProgressSlice';

/**
 * Devices to retry for a batch. Prefer the ids the batch reported as failed.
 * When none are known, only resubmit the whole batch if nothing succeeded
 * (a wholesale failure) — a partial success with unknown failed ids must not
 * re-activate the devices that already succeeded, so it yields no retry target.
 */
export const getRetryIds = (batch: ActivationBatch): string[] => {
  if (batch.failedPeripheralIds?.length) return batch.failedPeripheralIds;
  if (batch.success === 0) return batch.peripheralIds ?? [];
  return [];
};

/**
 * Whether a finished batch can be retried. Single predicate shared by the
 * completion card (whether to show "try again") and {@link useRetryActivationBatch}
 * (whether to actually resubmit), so the two can't diverge — e.g. showing the
 * button for a batch the retry flow would silently skip. Two independent
 * retry mechanisms satisfy this: the built-in peripheral one, which requires
 * the metadata needed to rebuild the request (`orgId`, `peripheralType`) and a
 * concrete, safe set of devices to retry; or a caller-supplied handler
 * registered for the batch's presentation group, which decides what's
 * retryable.
 */
export const isBatchRetryable = (batch: ActivationBatch): boolean =>
  !!batch.orgId &&
  batch.failure > 0 &&
  (!!getPresentationHandlers(getPresentationGroupKey(batch))?.onRetryBatches ||
    (!!batch.peripheralType && getRetryIds(batch).length > 0));
